// src/pages/BusinessAnalyticsPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import BusinessAnalytics from "../components/BusinessAnalytics";
import { db as defaultDb } from "../firebase";
import { resolveBusinessIdentifier, getBusinessRouteParam } from "../utils/businessSlug";

export default function BusinessAnalyticsPage({ db = defaultDb }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const businessParam = params?.businessId || null;
  const [businessId, setBusinessId] = useState(null);
  const [resolving, setResolving] = useState(true);
  const [resolveError, setResolveError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function resolve() {
      if (!businessParam) {
        if (mounted) {
          setBusinessId(null);
          setResolveError("No se recibió un negocio para mostrar analíticas.");
          setResolving(false);
        }
        return;
      }
      if (mounted) {
        setResolving(true);
        setResolveError("");
      }
      try {
        const result = await resolveBusinessIdentifier(db, businessParam);
        const realId = result.businessId || businessParam;
        if (mounted) {
          setBusinessId(realId);
          setResolving(false);
        }
        const routeParam = result.routeParam || getBusinessRouteParam(result.business || { id: realId });
        if (routeParam && String(routeParam) !== String(businessParam)) {
          navigate(`/business/${encodeURIComponent(routeParam)}/analytics${location.search || ""}`, { replace: true });
        }
      } catch (_) {
        if (mounted) {
          setBusinessId(businessParam);
          setResolveError("No pude validar el slug, pero intentaré cargar con el identificador recibido.");
          setResolving(false);
        }
      }
    }
    resolve();
    return () => { mounted = false; };
  }, [db, businessParam, navigate, location.search]);

  return (
    <main className="business-analytics-page">
      <style>{`
        .business-analytics-page {
          min-height: calc(100vh - 72px);
          overflow: visible;
          background:
            radial-gradient(circle at top left, rgba(0,103,95,0.07), transparent 32%),
            linear-gradient(180deg, #f8fafc 0%, #ffffff 52%, #f8fafc 100%);
          padding: max(14px, env(safe-area-inset-top)) max(14px, env(safe-area-inset-right)) max(18px, env(safe-area-inset-bottom)) max(14px, env(safe-area-inset-left));
        }

        .analytics-page-topbar {
          max-width: 1520px;
          margin: 0 auto 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .analytics-page-back {
          min-height: 42px;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #0f172a;
          padding: 0 14px;
          font-size: 13px;
          font-weight: 950;
          cursor: pointer;
          box-shadow: 0 10px 24px rgba(15,23,42,0.055);
        }

        .analytics-page-status {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .analytics-page-badge {
          border-radius: 999px;
          border: 1px solid rgba(0,103,95,0.16);
          background: rgba(0,103,95,0.07);
          color: #00675f;
          padding: 9px 12px;
          font-size: 12px;
          font-weight: 950;
        }

        .business-analytics-page .business-analytics {
          max-width: 1520px;
          margin: 0 auto;
          padding-left: 0;
          padding-right: 0;
        }



        .analytics-page-note {
          max-width: 1520px;
          margin: 0 auto 12px;
          border: 1px solid #dbeafe;
          background: #eff6ff;
          color: #1e3a8a;
          border-radius: 16px;
          padding: 11px 13px;
          font-size: 12px;
          font-weight: 850;
        }

        .analytics-page-loading {
          max-width: 1520px;
          margin: 0 auto;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          border-radius: 24px;
          min-height: 180px;
          display: grid;
          place-items: center;
          color: #64748b;
          font-size: 14px;
          font-weight: 900;
          box-shadow: 0 18px 48px rgba(15,23,42,.06);
        }
        @media (max-width: 720px) {
          .business-analytics-page { padding: 10px; }
          .analytics-page-topbar { margin-bottom: 8px; }
          .analytics-page-badge { display: none; }
        }
      `}</style>

      <div className="analytics-page-topbar">
        <button type="button" className="analytics-page-back" onClick={() => navigate(-1)}>
          ← Volver
        </button>
        <div className="analytics-page-status">
          {resolving ? <div className="analytics-page-badge">Cargando empresa…</div> : <div className="analytics-page-badge">Panel de empresa</div>}
        </div>
      </div>

      {resolveError ? <div className="analytics-page-note">{resolveError}</div> : null}
      {resolving ? <div className="analytics-page-loading">Preparando analíticas…</div> : <BusinessAnalytics businessId={businessId} db={db} />}
    </main>
  );
}
